// Job descriptions are typed into a plain textarea: heading lines
// ("The Role", "Requirements"), bullet lines ("•\tLead the team") and
// paragraphs, with or without blank lines and with stray \r. This turns that
// text into sections the page can lay out, without ever rendering HTML.

const BULLET = /^\s*(?:[•\-*●▪]|\d+[.)])\s+/;
const TRAILING = /[.,;!?]$/;

export const isBullet = (line) => BULLET.test(line);

export const isHeading = (line) =>
  line.length <= 60 &&
  line.split(/\s+/).length <= 8 &&
  !TRAILING.test(line) &&
  !isBullet(line);

const cleanLines = (text) =>
  String(text || "")
    .replace(/\r/g, "")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim());

const newSection = (heading) => ({ heading, blocks: [] });

const pushLine = (section, line) => {
  const last = section.blocks[section.blocks.length - 1];
  if (isBullet(line)) {
    const item = line.replace(BULLET, "");
    if (last?.type === "list") last.items.push(item);
    else section.blocks.push({ type: "list", items: [item] });
    return;
  }
  section.blocks.push({ type: "paragraph", text: line });
};

// A heading that only repeats the job title ("WE ARE HIRING! Grill Cook")
// adds nothing under a page that already shows the title.
const repeatsTitle = (line, title) =>
  Boolean(title) && line.toLowerCase().includes(title.toLowerCase());

export function parseDescription(text, title = "") {
  const sections = [];
  let current = newSection(null);

  cleanLines(text).forEach((line) => {
    if (!line) return;
    if (isHeading(line)) {
      if (current.heading || current.blocks.length) sections.push(current);
      current = newSection(repeatsTitle(line, title) ? null : line);
      return;
    }
    pushLine(current, line);
  });

  if (current.heading || current.blocks.length) sections.push(current);
  return sections.filter((section) => section.blocks.length);
}

const truncate = (text, limit) => {
  if (text.length <= limit) return text;
  const cut = text
    .slice(0, limit - 1)
    .replace(/\s+\S*$/, "")
    .replace(/[,;:\-.]+$/, "");
  return `${cut}…`;
};

// The paragraph under a "...Role..." heading is the most specific line in a
// description (several roles share the same company intro), so it wins.
export function summarise(text, limit = 170) {
  const sections = parseDescription(text);
  const paragraphsOf = (section) =>
    section.blocks.filter((b) => b.type === "paragraph").map((b) => b.text);

  const roleSection = sections.find((s) => /role/i.test(s.heading || ""));
  const rolePara = roleSection && paragraphsOf(roleSection).find((p) => p.length >= 60);
  const firstPara = sections.flatMap(paragraphsOf).find((p) => p.length >= 60);

  return truncate(rolePara || firstPara || "", limit);
}
